import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import Stack from '@mui/material/Stack';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import TranslateIcon from '@mui/icons-material/TranslateOutlined';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import { useAuth } from '../store/auth';
import { useLocale, useT } from '../i18n';

export function Login() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const { login, error } = useAuth();
  const navigate = useNavigate();
  const t = useT();
  const locale = useLocale(s => s.locale);
  const toggleLocale = useLocale(s => s.toggle);
  const nextLocale = locale === 'de' ? 'EN' : 'DE';
  const langTooltip = `${t.appBar.switchLanguage} → ${nextLocale}`;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    try {
      await login(username, password);
      navigate('/', { replace: true });
    } catch {
      // Fehler steht im Store
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'grid',
        placeItems: 'center',
        bgcolor: 'background.default',
        p: 2,
      }}
    >
      <Card sx={{ width: '100%', maxWidth: 400, position: 'relative' }}>
        <Tooltip title={langTooltip}>
          <IconButton
            onClick={toggleLocale}
            aria-label={langTooltip}
            sx={{ position: 'absolute', top: 8, right: 8 }}
          >
            <TranslateIcon fontSize="small" />
            <Box
              component="span"
              sx={{
                position: 'absolute',
                bottom: 2,
                right: 2,
                fontSize: 9,
                fontWeight: 700,
                lineHeight: 1,
                px: '3px',
                borderRadius: '4px',
                bgcolor: 'primary.main',
                color: 'primary.contrastText',
                pointerEvents: 'none',
              }}
            >
              {locale.toUpperCase()}
            </Box>
          </IconButton>
        </Tooltip>
        <CardContent sx={{ p: 4 }}>
          <Stack spacing={3} component="form" onSubmit={onSubmit} noValidate>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Box
                sx={{
                  width: 40, height: 40, borderRadius: '50%',
                  bgcolor: 'primary.main', color: 'primary.contrastText',
                  display: 'grid', placeItems: 'center',
                }}
              >
                <LockOutlinedIcon fontSize="small" />
              </Box>
              <Box>
                <Typography variant="h6" component="h1">{t.login.title}</Typography>
                <Typography variant="body2" color="text.secondary">{t.login.subtitle}</Typography>
              </Box>
            </Stack>

            <TextField
              label={t.login.username}
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              autoComplete="username"
              required
              autoFocus
            />
            <TextField
              label={t.login.password}
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              required
            />

            {error && <Alert severity="error" variant="outlined">{error}</Alert>}

            <Button
              type="submit"
              variant="contained"
              size="large"
              disabled={submitting}
              sx={{ alignSelf: 'stretch' }}
            >
              {submitting ? t.login.submitting : t.login.submit}
            </Button>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
}
